import React, { useState } from "react";
import Banner from "../../Components/userComponents/Common/Banner";
import { FaShareAlt, FaRegThumbsUp } from "react-icons/fa";
import { FiTag } from "react-icons/fi";

const ArticleDetails = () => {
  const article = {
    id: "a1",
    title: "How to Build a Modern Responsive Web App (2025)",
    banner:
      "https://images.unsplash.com/photo-1526378729708-8a7b3e7e9f0f?q=80&w=1400&auto=format&fit=crop&ixlib=rb-4.0.3&s=placeholder",
    excerpt:
      "A practical guide to building modern responsive applications using React, Tailwind, accessibility best practices, and deployment tips.",
    date: "December 4, 2025",
    tags: ["React", "Tailwind", "Frontend", "Accessibility"],
    content: [
      {
        type: "p",
        text: "Modern web apps require attention to performance, accessibility, and progressive enhancement. In this article we'll walk through the recommended stack and practical tips to ship fast, maintainable websites.",
      },
      {
        type: "h2",
        text: "1. Project Structure & Tooling",
      },
      {
        type: "p",
        text: "Start with a clear folder structure. Use a component-first approach and adopt conventions for assets, styles, and tests. Vite or Next.js are great starting points.",
      },
      {
        type: "h2",
        text: "2. Responsive Layouts with Tailwind",
      },
      {
        type: "p",
        text: "Tailwind CSS offers utility-first classes that speed up development. Use responsive variants and container classes for consistent spacing.",
      },
      {
        type: "blockquote",
        text: "Design for mobile first, then scale up — it forces clarity and reduces complexity.",
      },
      {
        type: "h2",
        text: "3. Accessibility (A11y)",
      },
      {
        type: "p",
        text: "Use semantic HTML, proper ARIA when needed, keyboard focus management, and make sure color contrast is sufficient.",
      },
    ],
  };

  const related = [
    {
      id: "r1",
      title: "Tailwind Tips: Utility Patterns",
      excerpt: "Small patterns that multiply your speed.",
      img: "",
    },
    {
      id: "r2",
      title: "Optimizing React Performance",
      excerpt: "Memoization, lazy loading, and profiling.",
      img: "",
    },
    {
      id: "r3",
      title: "Accessible Forms That Convert",
      excerpt: "Improve UX and accessibility for forms.",
      img: "",
    },
  ];

  // Comments state (local storage ready)
  const [comments, setComments] = useState(() => {
    try {
      const raw = localStorage.getItem("article_comments_" + article.id);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [liked, setLiked] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    const newComment = {
      id: Date.now().toString(),
      name: form.name.trim(),
      email: form.email.trim(),
      message: form.message.trim(),
      date: new Date().toISOString(),
    };
    const updated = [newComment, ...comments];
    setComments(updated);
    localStorage.setItem(
      "article_comments_" + article.id,
      JSON.stringify(updated)
    );
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <div>
      <Banner page="Article Details" path="articles" />

      {/* Page theme background */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef3ff] via-[#f7f5ff] to-white py-12 md:py-20">
        {/* Glow Orbs */}
        <div className="pointer-events-none absolute -top-40 -left-32 h-80 w-80 rounded-full bg-[#064585]/25 blur-3xl" />
        <div className="pointer-events-none absolute top-28 -right-40 h-96 w-96 rounded-full bg-[#5989C8]/25 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 md:px-8">
          {/* Article card */}
          <article
            className="bg-white/80 backdrop-blur rounded-3xl shadow-lg border border-[#5989C8]/15 overflow-hidden"
            data-aos="fade-up"
          >
            <div className="w-full h-64 md:h-96 relative">
              <img
                src={article.banner}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              {/* overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>

            <div className="p-6 md:p-10">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                    {article.title}
                  </h1>
                  <p className="mt-2 text-sm md:text-base text-slate-600 max-w-2xl">
                    {article.excerpt}
                  </p>

                  <div className="mt-4 flex items-center gap-3 text-sm text-slate-700">
                    <p className="text-xs text-slate-500">{article.date}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 md:gap-4">
                  <button
                    onClick={() => setLiked((s) => !s)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/60 backdrop-blur border border-[#5989C8]/10 hover:shadow-md transition"
                    aria-pressed={liked}
                  >
                    <FaRegThumbsUp
                      className={`text-${liked ? "blue-600" : "slate-700"}`}
                    />
                    <span className="text-sm font-medium">
                      {liked ? "Liked" : "Like"}
                    </span>
                  </button>

                  <button className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-[#5989C8] to-[#064585] text-white shadow-md hover:shadow-lg transition">
                    <FaShareAlt />
                    <span className="text-sm font-medium">Share</span>
                  </button>
                </div>
              </div>

              {/* Tags */}
              <div className="mt-5 flex flex-wrap gap-2">
                {article.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-2 bg-white/60 backdrop-blur px-3 py-1 rounded-full text-sm text-slate-700 border border-[#5989C8]/10"
                  >
                    <FiTag className="text-slate-600" />
                    {t}
                  </span>
                ))}
              </div>

              {/* Content */}
              <div className="mt-8 prose prose-slate max-w-none">
                {article.content.map((block, idx) => {
                  if (block.type === "p") return <p key={idx}>{block.text}</p>;
                  if (block.type === "h2")
                    return (
                      <h2 key={idx} className="mt-6">
                        {block.text}
                      </h2>
                    );
                  if (block.type === "blockquote")
                    return (
                      <blockquote
                        key={idx}
                        className="border-l-4 pl-4 italic text-slate-700"
                      >
                        {block.text}
                      </blockquote>
                    );
                  return null;
                })}
              </div>
            </div>
          </article>

          {/* Two-column: comments + related */}
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Comments column */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 border border-[#5989C8]/10 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">
                  Comments
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Join the discussion — be respectful and constructive.
                </p>

                {/* Comment form */}
                <form onSubmit={handleCommentSubmit} className="mt-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="p-3 rounded-xl border border-gray-200 bg-white/70 focus:outline-none"
                      required
                    />
                    <input
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="Email (optional)"
                      className="p-3 rounded-xl border border-gray-200 bg-white/70 focus:outline-none"
                      type="email"
                    />
                  </div>

                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Write a thoughtful comment..."
                    rows={4}
                    className="w-full p-3 rounded-xl border border-gray-200 bg-white/70 focus:outline-none"
                    required
                  />

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      {comments.length} comments
                    </div>
                    <button
                      className="bg-gradient-to-r from-[#5989C8] to-[#064585] text-white px-4 py-2 rounded-full shadow"
                      type="submit"
                    >
                      Post comment
                    </button>
                  </div>
                </form>
              </div>

              {/* Comments list */}
              <div className="space-y-4">
                {comments.length === 0 ? (
                  <div className="text-slate-600">
                    No comments yet — be the first to share your thoughts.
                  </div>
                ) : (
                  comments.map((c) => (
                    <div
                      key={c.id}
                      className="bg-white/80 backdrop-blur rounded-xl p-4 border border-[#5989C8]/10 shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#64a2ff] to-[#064585] flex items-center justify-center text-white font-semibold">
                          {c.name
                            .split(" ")
                            .map((n) => n[0])
                            .slice(0, 2)
                            .join("")}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">
                            {c.name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {new Date(c.date).toLocaleString()}
                          </div>
                          <p className="mt-2 text-slate-700">{c.message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Related column */}
            <aside className="space-y-6">
              <div className="bg-white/80 backdrop-blur rounded-2xl p-4 border border-[#5989C8]/10 shadow-sm">
                <h4 className="text-md font-semibold text-slate-900">
                  Related articles
                </h4>
                <ul className="mt-3 space-y-3">
                  {related.map((r) => (
                    <li key={r.id} className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-[#64a2ff] to-[#064585] flex items-center justify-center text-white font-semibold text-sm">
                        RD
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">
                          {r.title}
                        </div>
                        <div className="text-sm text-slate-600">
                          {r.excerpt}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white/80 backdrop-blur rounded-2xl p-4 border border-[#5989C8]/10 shadow-sm">
                <h4 className="text-md font-semibold text-slate-900">Tags</h4>
                <div className="mt-3 flex flex-wrap gap-2">
                  {article.tags.map((t) => (
                    <span
                      key={t}
                      className="text-sm bg-white/60 backdrop-blur px-3 py-1 rounded-full border border-[#5989C8]/10 text-slate-700"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArticleDetails;
